import { IconClose } from '../../../common/icons';
import { useModalDialog } from '../../../common/hooks/use-modal-dialog';
import { formatDateOnly } from '../../../common/utils/date';
import { useOpuSession, useOpuTransferSessions } from '../contexts/opu';
import { usePregnancy } from '../../pregnancies/contexts/pregnancies';

import '../../../common/styles/catalog.css';
import './styles/session-detail-modal.css';

const donorLabel = (d) => (d ? `${d.name || '—'}${d.number ? ` · ${d.number}` : ''}` : '—');
const recipientLabel = (r) => (r ? `${r.number ?? '—'}${r.name ? ` · ${r.name}` : ''}` : '—');
// Estado de preñez → color. En curso (ámbar) · Confirmada/Parto (verde) · Perdida (rojo).
const PREG_STATE = {
    open:      { label: 'En curso',   cls: 'cat-badge--1' },
    confirmed: { label: 'Confirmada', cls: 'cat-badge--2' },
    born:      { label: 'Parto',      cls: 'cat-badge--2' },
    lost:      { label: 'Perdida',    cls: 'cat-badge--0' },
};

// Badge de preñez con color y, si se PERDIÓ, la fase (dx negative → lost): "Perdida DX{n}".
// Solo las perdidas piden el detalle (diagnostics); las demás muestran su estado directo.
function PregnancyBadge({ pregnancy }) {
    const isLost = pregnancy?.state === 'lost';
    const { data } = usePregnancy(pregnancy?.id, { enabled: !!(isLost && pregnancy?.id) });
    if (!pregnancy) return <span className="cat-table__empty">—</span>;
    const base = PREG_STATE[pregnancy.state] || { label: pregnancy.state, cls: 'cat-badge--1' };
    let label = base.label;
    if (isLost) {
        const neg = (data?.diagnostics || []).find((d) => d.result === 'negative');
        label = neg?.number ? `Perdida DX${neg.number}` : 'Perdida';
    }
    return <span className={`cat-badge ${base.cls}`}>{label}</span>;
}

// Detalle de una sesión OPU en ventana emergente (admin-aspiration-sessions.md). Donadoras
// aspiradas + su funnel de clivage (view=resolution) y las transferencias (T.E.) que usaron sus
// embriones (/transfer-sessions). Read-only, aspirations:read.
export default function OpuSessionDetailModal({ sessionId, highlightDonorId, onClose }) {
    const { data: session, isLoading, error } = useOpuSession(sessionId, 'resolution');
    const { data: ts, isLoading: tsLoading } = useOpuTransferSessions(sessionId);
    const cardRef = useModalDialog(onClose);

    const aspirations = session?.aspirations || [];
    const transfers = (ts?.transfer_sessions || []).flatMap((s) => (s.transfers || []).map((t) => ({ ...t, teCode: s.code, teDate: s.date, teFarm: s.farm })));
    const fase = session?.resolution?.fase;

    return (
        <div className="osd-modal" role="dialog" aria-modal="true" aria-label="Detalle de sesión OPU">
            <div className="osd-modal__overlay" onClick={onClose} aria-hidden="true" />
            <div className="osd-modal__card" ref={cardRef}>
                <header className="osd-modal__head">
                    <div>
                        <h2 className="osd-modal__title">{session?.code || `Sesión OPU #${sessionId}`}</h2>
                        <span className="osd-modal__sub">
                            {session?.date ? formatDateOnly(session.date) : ''}
                            {session?.farm?.name ? ` · ${session.farm.name}` : ''}
                            {fase ? ` · ${fase}` : ''}
                        </span>
                    </div>
                    <button type="button" className="osd-modal__close" onClick={onClose} aria-label="Cerrar"><IconClose /></button>
                </header>

                {error ? (
                    <p className="osd-modal__state">{error.status === 403 ? 'Sin permiso para ver la sesión.' : 'No se pudo cargar la sesión.'}</p>
                ) : isLoading ? (
                    <p className="osd-modal__state">Cargando sesión…</p>
                ) : (
                    <div className="osd-body">
                        {/* Donadoras + clivage */}
                        <section className="osd-sec">
                            <h3 className="osd-sec__title">Donadoras y clivage <span className="cat-detail__count">{aspirations.length}</span></h3>
                            {!aspirations.length ? (
                                <p className="osd-modal__state">Sin aspiraciones.</p>
                            ) : (
                                <div className="osd-scroll">
                                    <table className="cat-table cat-table--compact">
                                        <thead>
                                            <tr><th>Donadora</th><th>Viables</th><th>Clivados</th><th>Embriones</th><th>Transf.</th><th>Preñeces</th><th>Crías</th></tr>
                                        </thead>
                                        <tbody>
                                            {aspirations.map((a) => {
                                                const r = a.resolution || {};
                                                const preg = (r.pregnancies_open || 0) + (r.pregnancies_confirmed || 0);
                                                const isSelf = highlightDonorId != null && String(a.donor?.id) === String(highlightDonorId);
                                                return (
                                                    <tr key={a.id} className={`cat-table__row ${isSelf ? 'osd-row--self' : ''}`}>
                                                        <td className="cat-table__name">{donorLabel(a.donor)}{isSelf && <span className="osd-self-tag">esta vaca</span>}</td>
                                                        <td>{r.viables ?? a.viables ?? '—'}</td>
                                                        <td>{r.oocytes_clivados ?? '—'}</td>
                                                        <td>{r.embryos_total ?? '—'}</td>
                                                        <td>{r.embryos_transferidos ?? '—'}</td>
                                                        <td>{preg || <span className="cat-table__empty">—</span>}</td>
                                                        <td>{r.births_count ?? '—'}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>

                        {/* Transferencias (T.E.) */}
                        <section className="osd-sec">
                            <h3 className="osd-sec__title">Transferencias (T.E.) <span className="cat-detail__count">{transfers.length}</span></h3>
                            {tsLoading ? (
                                <p className="osd-modal__state">Cargando transferencias…</p>
                            ) : !transfers.length ? (
                                <p className="osd-modal__state">Sin transferencias registradas.</p>
                            ) : (
                                <div className="osd-scroll">
                                    <table className="cat-table cat-table--compact">
                                        <thead>
                                            <tr><th>T.E.</th><th>Fecha</th><th>Finca</th><th>Receptora</th><th>Preñez</th></tr>
                                        </thead>
                                        <tbody>
                                            {transfers.map((t) => (
                                                <tr key={t.id} className="cat-table__row">
                                                    <td className="cat-table__name">{t.teCode || '—'}</td>
                                                    <td>{t.teDate ? formatDateOnly(t.teDate) : <span className="cat-table__empty">—</span>}</td>
                                                    <td>{t.teFarm?.name || <span className="cat-table__empty">—</span>}</td>
                                                    <td>{recipientLabel(t.recipient)}</td>
                                                    <td>{t.pregnancy ? <PregnancyBadge pregnancy={t.pregnancy} /> : <span className="cat-table__empty">—</span>}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>
                    </div>
                )}

                <footer className="osd-modal__foot">
                    <button type="button" className="osd-btn osd-btn--ghost" onClick={onClose}>Cerrar</button>
                </footer>
            </div>
        </div>
    );
}
